// src/pages/auth/ForgotPassword.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "@services/auth";
import { sanitizeText } from "@utils/sanitize";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!isValidEmail(email)) {
      setErrorMessage("Invalid email format.");
      return;
    }

    try {
      setLoading(true);
      const res = await forgotPassword(sanitizeText(email));
      setSuccessMessage(
        res?.message ||
          "If an account with that email exists, a password reset link has been sent."
      );
    } catch (err) {
      console.error("Forgot password error", err);
      setErrorMessage(
        err?.response?.data?.message ||
          "Something went wrong. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-80 z-50">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">
          Forgot your password?
        </h2>
        <p className="text-sm text-gray-500">
          Enter the email associated with your account and we&apos;ll send you a
          link to reset your password.
        </p>
      </div>

      <form className="space-y-4 text-left mt-4" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="email"
            className="block mb-1 text-sm font-medium text-gray-900"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
        </div>

        {errorMessage && (
          <p className="text-sm text-red-600 px-2">{errorMessage}</p>
        )}
        {successMessage && (
          <p className="text-sm text-green-600 px-2">{successMessage}</p>
        )}

        <button
          type="submit"
          className="w-full bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-lg"
        >
          Send Reset Link
        </button>

        <button
          type="button"
          onClick={() => navigate("/login")}
          className="w-full mt-2 text-sm text-blue-700 hover:underline"
        >
          Back to Login
        </button>
      </form>
    </>
  );
}
